import isAfter from "date-fns/isAfter";
import isBefore from "date-fns/isBefore";
import parse from "date-fns/parse";
import startOfDay from "date-fns/startOfDay";
import addDays from "date-fns/addDays";
import isEqual from "lodash/isEqual";
import get from "lodash/get";
import { validateFormattedStringAsDate } from "./date";

import { datePickerFormFormat } from "./formats";
import { parseDecimalField } from "./decimal";
import { isDateFormattedRegex, isEmailRegex, isPhoneNumberRegex } from "./regexes";

export const buildValidator = (predicate, errorMessage) => (value, values) =>
  predicate(value, values) ? undefined : errorMessage;

export const optionalCombineValidators =
  (...validators) =>
  (value, values) =>
    value
      ? validators.reduce(
          (errorMessage, validator) => errorMessage || validator(value, values),
          undefined
        )
      : undefined;

export const combineValidators =
  (...validators) =>
  (value, values) =>
    validators.reduce(
      (errorMessage, validator) => errorMessage || validator(value, values),
      undefined
    );

export const isEmail = errorMessage =>
  buildValidator(value => isEmailRegex.test((value || "").toLowerCase()), errorMessage);

export const minLength = (threshold, errorMessage) =>
  buildValidator(value => value.length >= threshold, errorMessage);

export const equalToField = (field, errorMessage) =>
  buildValidator((value, values) => value === values[field], errorMessage);

export const notEqualToValue = (forbiddenValue, errorMessage) =>
  buildValidator(value => value !== forbiddenValue, errorMessage);

export const maxLength = (threshold, errorMessage) =>
  buildValidator(value => value.length <= threshold, errorMessage);

export const hasLength = (threshold, errorMessage) =>
  buildValidator(value => value.length === threshold, errorMessage);

export const notEmpty = errorMessage =>
  buildValidator(value => value && value.length, errorMessage);

export const required = errorMessage =>
  buildValidator(
    value => typeof value === "boolean" || typeof value === "number" || !!value,
    errorMessage
  );

export const upperBound = (threshold, errorMessage) =>
  buildValidator(value => {
    const parsed = typeof value === "number" ? value : parseInt(value, 10);
    return (parsed || parsed === 0) && parsed <= threshold;
  }, errorMessage);

export const lowerBound = (threshold, errorMessage) =>
  buildValidator(value => {
    const parsed = typeof value === "number" ? value : parseInt(value, 10);
    return (parsed || parsed === 0) && parsed >= threshold;
  }, errorMessage);

export const isPositive = errorMessage =>
  buildValidator(value => parseFloat(value) > 0, errorMessage);

export const isTrue = errorMessage =>
  buildValidator(value => Boolean(value) === true, errorMessage);

export const isFutureDate = errorMessage =>
  buildValidator(value => {
    const today = startOfDay(new Date());
    const selectedDate = startOfDay(parse(value, datePickerFormFormat, today));

    return isAfter(selectedDate, today);
  }, errorMessage);

export const isNotFutureDate = errorMessage =>
  buildValidator(value => {
    const tomorrow = addDays(startOfDay(new Date()), 1);
    const selectedDate = startOfDay(parse(value, datePickerFormFormat, tomorrow));

    return isBefore(selectedDate, tomorrow);
  }, errorMessage);

export const isPhoneNumber = errorMessage =>
  buildValidator(value => isPhoneNumberRegex.test(value), errorMessage);

export const isPassword = (isPasswordRegex, errorMessage) =>
  buildValidator(value => isPasswordRegex.test(value), errorMessage);

export const isDateFormatted = errorMessage =>
  buildValidator(value => isDateFormattedRegex.test(value), errorMessage);

export const isDateValid = errorMessage =>
  buildValidator(value => validateFormattedStringAsDate(value), errorMessage);

export const isDateInCorrectRange = (lowerThreshold, upperThreshold, errorMessage) =>
  buildValidator(value => {
    const parsedDateString = parse(value, "dd.MM.yyyy", new Date());

    return (
      !isBefore(parsedDateString, lowerThreshold) && !isAfter(parsedDateString, upperThreshold)
    );
  }, errorMessage);

export const hasValue = (expectedValue, errorMessage) =>
  buildValidator(value => isEqual(value, expectedValue), errorMessage);

export const includesPurposesWithValue = (expectedValue, errorMessage) =>
  buildValidator(value => !(value.includes(expectedValue) && value.length > 1), errorMessage);

export const isFilledAndIsRelatedFieldFilled = (field, errorMessage) =>
  buildValidator((value, values) => get(values, field) && value, errorMessage);

export const isAnOptionFrom = (options, errorMessage) =>
  buildValidator(value => options.map(option => option.value).includes(value), errorMessage);

export const combineWithPrefillCheck =
  (requiredErrorMessage, ...validators) =>
  (value, values, meta) => {
    if (!meta.initial) {
      return optionalCombineValidators(...validators)(value, values);
    }
    return combineValidators(required(requiredErrorMessage), ...validators)(value, values);
  };

export const upperDecimalBound = (threshold, errorMessage) =>
  buildValidator(value => {
    const parsed = typeof value === "number" ? value : parseDecimalField(value);
    return (parsed || parsed === 0) && parsed <= threshold;
  }, errorMessage);

export const upperDecimalExcludedBound = (threshold, errorMessage) =>
  buildValidator(value => {
    const parsed = typeof value === "number" ? value : parseDecimalField(value);
    return (parsed || parsed === 0) && parsed < threshold;
  }, errorMessage);

export const lowerDecimalBound = (threshold, errorMessage) =>
  buildValidator(value => {
    const parsed = typeof value === "number" ? value : parseDecimalField(value);
    return (parsed || parsed === 0) && parsed >= threshold;
  }, errorMessage);

export const lowerDecimalBoundWithUndefined = (threshold, errorMessage) =>
  buildValidator(value => {
    if (value === undefined || value === "") return true;

    const parsed = typeof value === "number" ? value : parseDecimalField(value);
    return (parsed || parsed === 0) && parsed >= threshold;
  }, errorMessage);

export const upperDecimalBoundWithUndefined = (threshold, errorMessage) =>
  buildValidator(value => {
    if (value === undefined || value === "") return true;

    const parsed = typeof value === "number" ? value : parseDecimalField(value);
    return (parsed || parsed === 0) && parsed <= threshold;
  }, errorMessage);

export const excludeSelectValues = (excludingMap, errorMessage) =>
  buildValidator(value => !excludingMap.has(value), errorMessage);

export const emailAvailabilityValidator = (isAvailable, errorMessage) =>
  buildValidator(() => isAvailable, errorMessage);
